<script setup lang="ts">
  import { computed } from 'vue';
  import type { RouteLocationRaw } from 'vue-router';

  import type { IconName } from '../Icon/Icon.types';
  import Icon from '../Icon/Icon.vue';

  interface SectionHeaderProps {
    /**
     * The text label for the action button
     */
    actionLabel?: string;
    /**
     * The icon name for the action button
     */
    actionIcon?: IconName;
    /**
     * If the action button should be a router link, this is where it will link to.
     */
    to?: RouteLocationRaw;
  }

  const props = withDefaults(defineProps<SectionHeaderProps>(), {
    actionLabel: 'View All',
    actionIcon: 'arrow-right',
    to: undefined,
  });

  const emit = defineEmits<{
    /**
     * Emitted when the action button is clicked. Not emitted if the action button is a router link.
     */
    actionEvent: [];
  }>();

  const is = computed(() => {
    if (props.to) {
      return 'router-link';
    }

    return 'button';
  });
  const actionRootAttrs = computed(() => {
    if (props.to) {
      return {
        to: props.to,
      };
    }
    return {};
  });
</script>
<template>
  <div class="stash-section-header">
    <div
      class="stash-section-header__content flex-wrap items-center justify-between px-6 py-3 md:flex"
      data-test="stash-section-header|content"
    >
      <div data-test="stash-section-header|title">
        <h2>
          <!-- @slot Header content -->
          <slot></slot>
        </h2>
      </div>
      <component
        :is="is"
        v-bind="actionRootAttrs"
        data-test="stash-section-header|action"
        class="stash-section-header__action flex items-center gap-3 max-md:pt-3"
        @click="
          () => {
            if (!props.to) {
              emit('actionEvent');
            }
          }
        "
      >
        <span class="stash-section-header__action-label m-0 text-ice-900">
          {{ props.actionLabel }}
        </span>

        <span
          class="stash-section-header__action-icon size-6 items-center rounded-full bg-ice-200 p-[2px] text-ice-900"
        >
          <Icon :name="props.actionIcon" size="dense" />
        </span>
      </component>
    </div>
    <div class="stash-section-header__divider h-px bg-ice-200" />
  </div>
</template>
